import { useEffect, useMemo, useState } from 'react';
import { AuthService, UserDirectoryItem } from '../services/AuthService';

interface UseUserDirectoryResult {
  /** 사용자 디렉토리. 로드 실패 시 빈 배열. */
  users: UserDirectoryItem[];
  /** 이름 문자열 배열 — Autocomplete options 로 바로 사용. */
  names: string[];
  loading: boolean;
  error: string | null;
}

/**
 * 가입된 사용자의 디렉토리(id/name/email/role)를 한 번 가져와 캐싱한다.
 * 방문자/담당자 선택 등 autocomplete picker 용도.
 * - 같은 세션 내에서 한 번만 호출 (module-level cache).
 * - 실패해도 폼은 freeSolo 자유 입력이 가능하므로 silent fallback.
 */
let cache: UserDirectoryItem[] | null = null;
let inflight: Promise<UserDirectoryItem[]> | null = null;

const useUserDirectory = (): UseUserDirectoryResult => {
  const [users, setUsers] = useState<UserDirectoryItem[]>(cache ?? []);
  const [loading, setLoading] = useState<boolean>(cache == null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (cache) {
      setUsers(cache);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const promise = inflight ?? AuthService.listUsers();
    inflight = promise;
    promise
      .then((list) => {
        cache = list;
        if (!cancelled) setUsers(list);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : '사용자 목록을 불러올 수 없습니다');
      })
      .finally(() => {
        inflight = null;
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const names = useMemo(() => users.map((u) => u.name).filter(Boolean), [users]);
  return { users, names, loading, error };
};

export default useUserDirectory;
